import { h, Fragment } from 'preact';
import { useCallback, useState, useEffect, useRef } from 'preact/hooks';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Zoom } from 'swiper';
import Image from 'utils/image';
import Video from 'utils/reactVideo';
import useWindowSize from 'utils/use-window-size';
import ImageZoom from 'react-image-zooom';
import { StyleTransition } from 'preact-transitioning';
import { useProduct } from './product-context';
import 'swiper/css';
import 'swiper/css/zoom';
import './product-gallery.css';
import ProductModels from './product-models';

const ProductGalleryMedia = ({
  media,
  handleImageLoad,
  handleVideoLoad,
}) => {
  const mediaType = media.mediaContentType.toLowerCase();
  const [vimeoPlayer, setVimeoPlayer] = useState(null);
  const { isMobile } = useWindowSize();
  const onPlayerInit = useCallback((player) => setVimeoPlayer(player), []);

  if (mediaType === 'image') {
    return (
      <div className={`aspect-[11/14] relative ${isMobile ? 'swiper-zoom-container' : ''}`}>
        {isMobile ? (
          <Image
          src={media.previewImage.transformedSrc}
          alt={media.image.altText}
          isTransformed={true}
          srcWidths={{ sm: 40, md: 168, lg: 324, xl: 480, '2xl': 536 }}
          additionalClasses="relative swiper-zoom-target z-1 object-cover h-full w-full"
        />
        ) : (
          <StyleTransition
              in={true}
              duration={300}
              styles={{
                enter: { opacity: 0 },
                enterActive: { opacity: 1 },
              }}
            >
            <ImageZoom
              src={media.previewImage.transformedSrc}
              alt={media.image.altText}
              style={{ transition: 'opacity 300ms' }}
              className="!absolute z-1 object-cover h-full !w-full [&>img]:w-full [&>img]:h-full [&>img]:object-cover"
              width="100%"
              height="auto"
            />
          </StyleTransition>
        )}
        <div class="absolute top-0 w-full h-full aspect-[11/14] bg-gray-100"></div>
      </div>
    );
  }

  if (mediaType === 'external_video' && media.host.toLowerCase() === 'vimeo') {
    return (
      <div className="aspect-[11/14] relative z-1">
        <Video
          className="h-full w-full relative z-1"
          autoplay={true}
          loop={true}
          externalId={media.originUrl}
          id={media.id}
          onPlayerInit={onPlayerInit}
        />
        <div class="absolute top-0 w-full h-full aspect-[11/14] bg-gray-100"></div>
      </div>
    );
  }

  if (mediaType === 'video' && media.sources) {
    return (
      <div className="aspect-[11/14] relative z-1">
        <Video
          className="h-full w-full relative z-1"
          autoplay={true}
          loop={true}
          isHTML5={true}
          id={media.id}
          videoSrc={media.sources[1].url}
        />
      </div>
    );
  }
};

const ProductGallery = (props) => {
  const { activeProduct } = useProduct();
  const { isMobile } = useWindowSize();
  const { productModels } = useProduct();

  const swiperOptions = {
    slidesPerView: 1.2,
    zoom: {
      maxRatio: 5,
    },
  };

  const [lastMediaBottom, setLastMediaBottom] = useState(0);
  const mediaContainerRef = useRef(null);
  const mediaRefs = useRef([]);

  const desktopMedia = () =>
    activeProduct?.media?.edges?.map((item, idx) => (
      <div ref={(el) => {
        mediaRefs.current[idx] = el;
      }} key={item.node.id}>
        <ProductGalleryMedia media={item.node} />
      </div>
    ));

  useEffect(() => {
    setTimeout(() => {
      if (mediaRefs.current.length > 0) {
        const medias = mediaRefs.current.filter(el => el);
        const lastEl = medias[medias.length - 1];
        if (lastEl) {
          const containerRect = mediaContainerRef.current.getBoundingClientRect();
          const rect = lastEl.getBoundingClientRect();
          setLastMediaBottom((containerRect.bottom - rect.bottom) + 130);
        }
      }
    }, 2000);
  }, [activeProduct]);

  useEffect(() => {
    if (document.querySelector('.not-loaded')) {
      document
        .querySelector('[data-section="product"]')
        .classList.remove('not-loaded');
    }
  });

  return (
    <>
      {!isMobile && (
        <div
          ref={mediaContainerRef}
          className={`relative grid grid-cols-2 gap-px auto-rows-min h-min ${
            productModels.length > 0 && '-top-[1px]'
          }`}
        >
          {productModels.length > 0 && (
            <ProductModels
              styles="absolute col-span-2 z-10 bottom-12 mx-auto -translate-x-1/2 left-1/2"
              customStyle={{ marginTop: `-${lastMediaBottom}px` }}
            />
          )}
          {desktopMedia()}
        </div>
      )}
      {isMobile && (
        <>
          <Swiper {...swiperOptions} allowTouchMove={true} modules={[Zoom]}>
            {activeProduct.media.edges.map((item) => (
              <SwiperSlide key={item.node.id}>
                <ProductGalleryMedia media={item.node} />
              </SwiperSlide>
            ))}
          </Swiper>
          {productModels.length > 0 && (
            <ProductModels styles="relative bottom-0 z-10 mx-auto" />
          )}
        </>
      )}
    </>
  );
};

export default ProductGallery;
